import type { ReservationSetting, ServiceOpeningHour, ServiceOpeningSchedule, ServiceOpeningDay } from '../models/data';

export const MAX_SERVICE_OPENING_HOURS = 4;
export const MAX_SERVICE_OPENING_SCHEDULES = 7;

const ALL_DAYS: ServiceOpeningDay[] = [0, 1, 2, 3, 4, 5, 6];
const VALID_DAYS = new Set<ServiceOpeningDay>(ALL_DAYS);
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const normalizeTime = (value: unknown): string =>
  typeof value === 'string' && TIME_PATTERN.test(value) ? value : '';

const normalizeDayList = (value: unknown): ServiceOpeningDay[] => {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value.filter((day): day is ServiceOpeningDay =>
        typeof day === 'number' && VALID_DAYS.has(day as ServiceOpeningDay)
      )
    )
  ).sort((a, b) => a - b);
};

const normalizeLegacyDays = (value: unknown): ServiceOpeningDay[] => {
  const days = normalizeDayList(value);
  return days.length > 0 ? days : [...ALL_DAYS];
};

const normalizeCanonicalScheduleDays = (value: unknown): ServiceOpeningDay[] =>
  normalizeDayList(value);

const normalizeSchedule = (value: unknown, index: number): ServiceOpeningSchedule | null => {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<ServiceOpeningSchedule> & Record<string, unknown>;
  return {
    id: typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `service-opening-schedule-${index + 1}`,
    days: normalizeCanonicalScheduleDays(item.days),
    opensAt: normalizeTime(item.opensAt),
    closesAt: normalizeTime(item.closesAt),
  };
};

const normalizeServiceOpeningHour = (value: unknown, index: number): ServiceOpeningHour | null => {
  if (!value || typeof value !== 'object') return null;
  const item = value as Partial<ServiceOpeningHour> & Record<string, unknown>;
  const name = typeof item.name === 'string' ? item.name.trim() : '';
  const nameEn = typeof item.nameEn === 'string' ? item.nameEn.trim() : '';
  if (!name) return null;

  const schedules = Array.isArray(item.schedules)
    ? item.schedules
      .map((schedule, scheduleIndex) => normalizeSchedule(schedule, scheduleIndex))
      .filter((schedule): schedule is ServiceOpeningSchedule => Boolean(schedule))
      .slice(0, MAX_SERVICE_OPENING_SCHEDULES)
    : [
      {
        id: `${typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `service-opening-${index + 1}`}-schedule-1`,
        days: normalizeLegacyDays(item.days),
        opensAt: normalizeTime(item.opensAt),
        closesAt: normalizeTime(item.closesAt),
      },
    ];

  return {
    id: typeof item.id === 'string' && item.id.trim() ? item.id.trim() : `service-opening-${index + 1}`,
    name,
    ...(nameEn ? { nameEn } : {}),
    schedules,
    isEnabled: item.isEnabled !== false,
  };
};

export const createDefaultServiceOpeningSchedule = (): ServiceOpeningSchedule => ({
  id: `service-opening-schedule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  days: [...ALL_DAYS],
  opensAt: '10:00',
  closesAt: '22:00',
});

const legacyServiceOpeningHoursFromSettings = (settings: Partial<ReservationSetting>): ServiceOpeningHour[] => {
  const items: ServiceOpeningHour[] = [];
  const kitchenStartTime = settings.kitchenStartTime ?? (settings as any).kitchenOpen ?? null;
  const kitchenEndTime = settings.kitchenEndTime ?? null;
  const barStartTime = settings.barStartTime ?? null;
  const barEndTime = settings.barEndTime ?? (settings as any).barClose ?? null;

  if (kitchenStartTime || kitchenEndTime) {
    items.push({
      id: 'legacy-kitchen',
      name: 'Konyha',
      nameEn: 'Kitchen',
      schedules: [{
        id: 'legacy-kitchen-schedule-1',
        days: [...ALL_DAYS],
        opensAt: normalizeTime(kitchenStartTime),
        closesAt: normalizeTime(kitchenEndTime),
      }],
      isEnabled: true,
    });
  }

  if (barStartTime || barEndTime) {
    items.push({
      id: 'legacy-bar',
      name: 'Bár',
      nameEn: 'Bar',
      schedules: [{
        id: 'legacy-bar-schedule-1',
        days: [...ALL_DAYS],
        opensAt: normalizeTime(barStartTime),
        closesAt: normalizeTime(barEndTime),
      }],
      isEnabled: true,
    });
  }

  return items.slice(0, MAX_SERVICE_OPENING_HOURS);
};

export const normalizeServiceOpeningHours = (settings: Partial<ReservationSetting> | null | undefined): ServiceOpeningHour[] => {
  if (!settings) return [];
  if (Array.isArray(settings.serviceOpeningHours)) {
    return settings.serviceOpeningHours
      .map((item, index) => normalizeServiceOpeningHour(item, index))
      .filter((item): item is ServiceOpeningHour => Boolean(item))
      .slice(0, MAX_SERVICE_OPENING_HOURS);
  }

  return legacyServiceOpeningHoursFromSettings(settings);
};

export const validateServiceOpeningHours = (items: ServiceOpeningHour[]): string | null => {
  if (items.length > MAX_SERVICE_OPENING_HOURS) {
    return `Legfeljebb ${MAX_SERVICE_OPENING_HOURS} nyitvatartási opció menthető.`;
  }

  for (const item of items) {
    if (!item.name.trim()) {
      return 'Minden tájékoztató nyitvatartásnál kötelező a név megadása.';
    }

    if (!Array.isArray(item.schedules) || item.schedules.length === 0) {
      return 'Minden tájékoztató nyitvatartáshoz legalább egy nyitvatartási sor szükséges.';
    }

    if (item.schedules.length > MAX_SERVICE_OPENING_SCHEDULES) {
      return `Egy opción belül legfeljebb ${MAX_SERVICE_OPENING_SCHEDULES} nyitvatartási sor menthető.`;
    }

    const assignedDays = new Set<ServiceOpeningDay>();
    for (const schedule of item.schedules) {
      if (!TIME_PATTERN.test(schedule.opensAt) || !TIME_PATTERN.test(schedule.closesAt)) {
        return 'A nyitás és zárás idejét HH:mm formátumban kell megadni.';
      }
      if (!Array.isArray(schedule.days) || schedule.days.length === 0 || schedule.days.length > 7) {
        return 'Minden nyitvatartási sornál legalább egy, legfeljebb hét aktív napot kell választani.';
      }
      for (const day of schedule.days) {
        if (!VALID_DAYS.has(day)) {
          return 'Az aktív napok csak 0 és 6 közötti értékek lehetnek.';
        }
        if (assignedDays.has(day)) {
          return 'Egy opción belül ugyanaz a nap csak egy nyitvatartási sorhoz rendelhető.';
        }
        assignedDays.add(day);
      }
    }
  }

  return null;
};

export const formatServiceOpeningDays = (days: ServiceOpeningDay[] | undefined): string => {
  if (!days || days.length === 0 || days.length === 7) return '';
  const labels = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];
  const sortedDays = Array.from(new Set(days)).sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sortedDays[0];
  let previous = sortedDays[0];

  for (let index = 1; index <= sortedDays.length; index += 1) {
    const current = sortedDays[index];
    if (current === previous + 1) {
      previous = current;
      continue;
    }

    ranges.push(start === previous ? labels[start] : `${labels[start]}–${labels[previous]}`);
    start = current;
    previous = current;
  }

  return ranges.join(', ');
};
